import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Music, Play, Pause, SkipForward, SkipBack, Shuffle, Repeat, LogOut, Smartphone, Radio, RefreshCw } from 'lucide-react';
import { useSpotify } from '../SpotifyContext';

export const SpotifyControls = () => {
  const {
    isConnected,
    userProfile,
    playbackState,
    isLoading,
    loginSpotify,
    disconnectSpotify,
    toggleShuffle,
    setRepeatMode,
    setCrossfade,
    pausePlayback,
    resumePlayback,
    nextTrack,
    previousTrack,
    checkStoredTokens
  } = useSpotify();

  const [isExpanded, setIsExpanded] = useState(false);
  const [crossfadeValue, setCrossfadeValue] = useState(0);
  const [isChecking, setIsChecking] = useState(false);

  const handleVerify = async () => {
    setIsChecking(true);
    try {
      await checkStoredTokens();
    } finally {
      setIsChecking(false);
    }
  };

  if (!isConnected) {
    return (
      <div className="flex items-center gap-2">
        <Button
          onClick={loginSpotify}
          disabled={isLoading}
          className="bg-green-600 hover:bg-green-700 text-white"
          size="sm"
        >
          <Music className="w-4 h-4 mr-2" />
          {isLoading ? 'Conectando...' : 'Conectar Spotify'}
        </Button>
        
        {/* Botón verificar siempre visible para PWA */}
        <Button
          onClick={handleVerify}
          disabled={isChecking}
          variant="outline"
          size="sm"
          className="border-slate-600 text-slate-300 hover:bg-slate-700"
          title="Pulsa aquí después de autenticar en Spotify"
        >
          {isChecking ? (
            <RefreshCw className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
        </Button>
      </div>
    );
  }

  return (
    <Card className="bg-slate-800 border-slate-700">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white flex items-center gap-2 text-base">
            <Music className="w-5 h-5 text-green-400" />
            Spotify Connected
          </CardTitle>
          <Button
            onClick={disconnectSpotify}
            variant="ghost"
            size="sm"
            className="text-slate-400 hover:text-white"
          >
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {/* User Info */}
        <div className="flex items-center gap-3 p-2 bg-slate-900/50 rounded">
          {userProfile?.images?.[0]?.url && (
            <img
              src={userProfile.images[0].url}
              alt="Profile"
              className="w-10 h-10 rounded-full"
            />
          )}
          <div className="flex-1">
            <p className="text-white text-sm font-medium">{userProfile?.display_name}</p>
            <p className="text-slate-400 text-xs">
              {userProfile?.is_premium ? '✨ Premium' : 'Free'}
            </p>
          </div>
        </div>

        {/* Current Track */}
        {playbackState?.current_track && (
          <div className="p-2 bg-green-900/20 border border-green-700/30 rounded">
            <div className="flex items-center gap-2 mb-1">
              <Music className="w-4 h-4 text-green-400" />
              <span className="text-green-300 text-xs font-medium">Reproduciendo ahora</span>
            </div>
            <p className="text-white text-sm font-medium truncate">
              {playbackState.current_track.name}
            </p>
            <p className="text-slate-400 text-xs truncate">
              {playbackState.current_track.artists.join(', ')}
            </p>
          </div>
        )}

        {/* Device */}
        {playbackState?.device && (
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <Smartphone className="w-3 h-3" />
            <span>{playbackState.device.name}</span>
          </div>
        )}

        {/* Playback Controls */}
        {userProfile?.is_premium && (
          <div className="space-y-2">
            {/* Main Controls */}
            <div className="flex items-center justify-center gap-2">
              <Button
                onClick={previousTrack}
                variant="outline"
                size="sm"
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              >
                <SkipBack className="w-4 h-4" />
              </Button>
              
              <Button
                onClick={playbackState?.is_playing ? pausePlayback : resumePlayback}
                className="bg-green-600 hover:bg-green-700 text-white"
                size="sm"
              >
                {playbackState?.is_playing ? (
                  <Pause className="w-4 h-4" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
              </Button>
              
              <Button
                onClick={nextTrack}
                variant="outline"
                size="sm"
                className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
              >
                <SkipForward className="w-4 h-4" />
              </Button>
            </div>

            {/* Toggle Controls */}
            <div className="flex items-center justify-center gap-2">
              <Button
                onClick={() => toggleShuffle(!playbackState?.shuffle_state)}
                variant="outline"
                size="sm"
                className={`${
                  playbackState?.shuffle_state
                    ? 'bg-green-600 border-green-600 text-white'
                    : 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600'
                }`}
              >
                <Shuffle className="w-3.5 h-3.5 mr-1" />
                Mix
              </Button>
              
              <Button
                onClick={() => {
                  const states = ['off', 'context', 'track'];
                  const currentIndex = states.indexOf(playbackState?.repeat_state || 'off');
                  const nextState = states[(currentIndex + 1) % states.length];
                  setRepeatMode(nextState);
                }}
                variant="outline"
                size="sm"
                className={`${
                  playbackState?.repeat_state !== 'off'
                    ? 'bg-green-600 border-green-600 text-white'
                    : 'bg-slate-700 border-slate-600 text-white hover:bg-slate-600'
                }`}
              >
                <Repeat className="w-3.5 h-3.5 mr-1" />
                {playbackState?.repeat_state === 'track' ? '1' : 
                 playbackState?.repeat_state === 'context' ? 'All' : 'Off'}
              </Button>
            </div>

            {/* Crossfade Control */}
            <div className="space-y-2 pt-2 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Radio className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs text-slate-400">Crossfade</span>
                </div>
                <span className="text-xs text-white font-medium">{crossfadeValue}s</span>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min="0"
                  max="12"
                  value={crossfadeValue}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    setCrossfadeValue(value);
                  }}
                  onMouseUp={(e) => {
                    const value = parseInt(e.target.value);
                    setCrossfade(value * 1000); // Convert to milliseconds
                  }}
                  className="flex-1 h-1 bg-slate-700 rounded-lg appearance-none cursor-pointer slider"
                  style={{
                    background: `linear-gradient(to right, #10b981 0%, #10b981 ${(crossfadeValue / 12) * 100}%, #334155 ${(crossfadeValue / 12) * 100}%, #334155 100%)`
                  }}
                />
                
                <Button
                  onClick={() => {
                    setCrossfadeValue(0);
                    setCrossfade(0);
                  }}
                  variant="ghost"
                  size="sm"
                  className="text-slate-400 hover:text-white h-7 px-2"
                  title="Desactivar crossfade"
                >
                  Off
                </Button>
              </div>
              
              <div className="flex gap-1">
                {[0, 3, 6, 12].map(val => (
                  <Button
                    key={val}
                    onClick={() => {
                      setCrossfadeValue(val);
                      setCrossfade(val * 1000);
                    }}
                    variant="ghost"
                    size="sm"
                    className="flex-1 text-xs text-slate-400 hover:text-white h-6 px-1"
                  >
                    {val}s
                  </Button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Playback mode toggle removed - all links now open external Spotify */}
      </CardContent>
    </Card>
  );
};
